// src/pages/dashboard/VerificationDashboard.js
// Trust & Badges — practitioner's verification status, earned badges, quality metrics.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { VerifiedBadge, BackgroundCheckBadge, ExcellenceBadge } from '../../components/shared/BadgeDisplay';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return null; }
};

const QUALITY_STATUS_DISPLAY = {
  healthy: { label: 'Healthy', color: '#059669', bg: '#D1FAE5', icon: '✓' },
  watch:   { label: 'Watch', color: '#D97706', bg: '#FEF3C7', icon: '⚠️' },
  warning: { label: 'Warning', color: '#DC2626', bg: '#FEE2E2', icon: '⚠️' },
  review:  { label: 'Under Review', color: '#991B1B', bg: '#FEE2E2', icon: '🚨' },
};

// ─── Main component ──────────────────────────────────────────────────────────

const VerificationDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [verification, setVerification] = useState(null);
  const [reviewStats, setReviewStats] = useState(null);
  const [qualityStatus, setQualityStatus] = useState('healthy');

  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 5000);

    const unsub = onAuthStateChanged(auth, (user) => {
      if (!done && user) {
        done = true;
        clearTimeout(timeout);
        const practRef = doc(db, 'practitioners', user.uid);
        const unsubFirestore = onSnapshot(practRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setVerification(data.verification || null);
            setReviewStats(data.reviewStats || null);
            setQualityStatus(data.qualityStatus || 'healthy');
          }
          setLoading(false);
        }, () => { setLoading(false); });
        return () => unsubFirestore();
      } else if (!done && user === null) {
        done = true;
        clearTimeout(timeout);
        setLoading(false);
      }
    });

    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const avgRating = reviewStats?.averageRating || 0;
  const totalReviews = reviewStats?.totalReviews || 0;
  const qualityDisplay = QUALITY_STATUS_DISPLAY[qualityStatus] || QUALITY_STATUS_DISPLAY.healthy;

  return (
    <div className="min-h-screen bg-gray-50 pt-20" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Trust & Badges</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>
            Trust & Badges
          </h1>
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5 }}>
            Badges help seekers feel confident booking with you. Here's your verification status.
          </p>
        </div>

        {/* ── Your Badges ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-5">Your badges</h2>

          {verification?.identityVerified ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <div style={{ flexShrink: 0, marginTop: '2px' }}>
                <VerifiedBadge size={28} />
              </div>
              <div>
                <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: '0 0 2px 0' }}>
                  Verified
                </p>
                {verification.identityVerifiedAt && (
                  <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 4px 0' }}>
                    Earned {formatDate(verification.identityVerifiedAt)}
                  </p>
                )}
                <p style={{ fontSize: '13px', color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
                  Identity confirmed. Profile reviewed by Divine Frameworks.
                </p>
              </div>
            </div>
          ) : verification?.manualReviewStatus === 'pending' ? (
            <div style={{
              padding: '14px 16px', background: '#f5f3ff', border: '1px solid #ddd6fe',
              borderRadius: '8px',
            }}>
              <p style={{ fontSize: '13px', color: '#5b21b6', margin: 0, lineHeight: 1.5 }}>
                Your profile is being reviewed. You'll earn your Verified badge once approved — this usually takes less than 24 hours.
              </p>
            </div>
          ) : verification?.manualReviewStatus === 'rejected' ? (
            <div style={{
              padding: '14px 16px', background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: '8px',
            }}>
              <p style={{ fontSize: '13px', color: '#991B1B', margin: '0 0 4px 0', fontWeight: 600 }}>
                Your profile needs updates before verification.
              </p>
              {verification.rejectionFeedback && (
                <p style={{ fontSize: '13px', color: '#991B1B', margin: '0 0 8px 0', lineHeight: 1.5 }}>
                  Feedback: "{verification.rejectionFeedback}"
                </p>
              )}
              <Link to="/dashboard/settings/profile" style={{ fontSize: '13px', fontWeight: 600, color: '#7c3aed', textDecoration: 'none' }}>
                Update my profile →
              </Link>
            </div>
          ) : (
            <p style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic' }}>
              No badges earned yet. Complete your profile to start the verification process.
            </p>
          )}
        </div>

        {/* ── Available Badges ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-5">Available</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Badge 2 — Background Checked */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <div style={{ flexShrink: 0, marginTop: '2px', opacity: 0.5 }}>
                <BackgroundCheckBadge size={28} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: '0 0 2px 0' }}>
                  Background Checked
                </p>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 10px 0', lineHeight: 1.5 }}>
                  Stand out with a third-party background check. One-time cost: $29.99
                </p>
                <span style={{
                  display: 'inline-block', padding: '6px 16px',
                  background: '#F3F4F6', color: '#9CA3AF',
                  borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                  fontFamily: "'Montserrat', sans-serif",
                  cursor: 'not-allowed',
                }}>
                  Coming soon
                </span>
              </div>
            </div>

            {/* Badge 3 — Excellence */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' }}>
              <div style={{ flexShrink: 0, marginTop: '2px', opacity: 0.5 }}>
                <ExcellenceBadge size={28} />
              </div>
              <div>
                <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: '0 0 2px 0' }}>
                  Practitioner of Excellence
                </p>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 8px 0', lineHeight: 1.5 }}>
                  Awarded by invitation for exceptional professionalism. Here's what we look for:
                </p>
                <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {[
                    '4.5+ average rating',
                    'Active, professional profile',
                    'Consistent positive client feedback',
                    'Regular engagement with the platform',
                  ].map((item) => (
                    <li key={item} style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.4 }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ── Your Quality ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-5">Your quality</h2>

          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div>
              <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 2px 0' }}>Average rating</p>
              <p style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0 }}>
                {totalReviews > 0 ? avgRating.toFixed(1) : '—'}
                {totalReviews > 0 && <span style={{ fontSize: '14px', color: '#F59E0B', marginLeft: '4px' }}>★</span>}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 2px 0' }}>Total reviews</p>
              <p style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0 }}>
                {totalReviews}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 2px 0' }}>Quality status</p>
              <span style={{
                display: 'inline-block',
                padding: '3px 10px',
                background: qualityDisplay.bg,
                color: qualityDisplay.color,
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: 600,
              }}>
                {qualityDisplay.icon} {qualityDisplay.label}
              </span>
            </div>
          </div>

          {(qualityStatus === 'watch' || qualityStatus === 'warning') && (
            <div style={{
              padding: '12px 14px', background: '#FFFBEB',
              border: '1px solid #FDE68A', borderRadius: '8px',
            }}>
              <p style={{ fontSize: '13px', color: '#92400E', margin: '0 0 6px 0', lineHeight: 1.5 }}>
                {qualityStatus === 'watch'
                  ? 'Your rating has dipped below 4.0. A few great sessions can bring it back up!'
                  : 'Your rating is below 3.0. We want to help you improve — check out our resources.'}
              </p>
              <Link to="/business-playbook" style={{ fontSize: '13px', fontWeight: 600, color: '#7c3aed', textDecoration: 'none' }}>
                View tips to improve →
              </Link>
            </div>
          )}

          {qualityStatus === 'healthy' && totalReviews >= 5 && (
            <p style={{ fontSize: '13px', color: '#059669', margin: 0 }}>
              You're doing great! Keep up the excellent work.
            </p>
          )}

          {totalReviews < 5 && (
            <p style={{ fontSize: '13px', color: '#9CA3AF', margin: 0, fontStyle: 'italic' }}>
              Quality status will be calculated once you have 5+ reviews.
            </p>
          )}
        </div>

      </div>
    </div>
  );
};

export default VerificationDashboard;
